/**
 * Experiência de uso do painel (I1–I5) num lugar só.
 *
 * Guarda o que é PREFERÊNCIA DA PESSOA (densidade, dicas fechadas, novidade vista — todas
 * no servidor, por usuário) e o estado dos quatro diálogos que qualquer tela pode abrir:
 * busca (⌘K), atalhos (?), sugestão e novidades. Os atalhos de teclado também nascem
 * aqui, uma vez, no `document`.
 *
 * A densidade tem um espelho no `localStorage` só para o primeiro quadro não piscar do
 * confortável para o compacto enquanto a preferência viaja; quem manda é o servidor.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { DensityProvider, useToast } from '@mgdlead/ui';
import type { UiDensity } from '@mgdlead/ui';

import { humanizeError } from '../../lib/errors';
import { LATEST_CHANGELOG_ID, hasUnreadChangelog } from './changelog';
import {
  useDismissHint,
  useMarkChangelogSeen,
  usePreferences,
  useResetHints,
  useSetDensity,
} from './experience-api';
import { createShortcutReader, isTextField, type ShortcutAction } from './shortcuts';

export const DENSITY_STORAGE_KEY = 'mgdlead.density';

function readStoredDensity(): UiDensity {
  try {
    return window.localStorage.getItem(DENSITY_STORAGE_KEY) === 'compact'
      ? 'compact'
      : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

function storeDensity(value: UiDensity): void {
  try {
    window.localStorage.setItem(DENSITY_STORAGE_KEY, value);
  } catch {
    // aba anônima / armazenamento bloqueado: a preferência continua no servidor
  }
}

export type ExperienceDialog = 'palette' | 'shortcuts' | 'feedback' | 'news' | null;

export interface ExperienceContextValue {
  density: UiDensity;
  setDensity: (value: UiDensity) => void;
  /** `undefined` enquanto as preferências não chegaram: nenhuma dica pisca antes da hora. */
  isHintDismissed: (hintId: string) => boolean | undefined;
  dismissHint: (hintId: string) => void;
  resetHints: () => void;
  hasNews: boolean;
  markNewsSeen: () => void;
  npsDue: boolean;
  dialog: ExperienceDialog;
  openDialog: (dialog: Exclude<ExperienceDialog, null>) => void;
  closeDialog: () => void;
}

const ExperienceContext = createContext<ExperienceContextValue | null>(null);

export function useExperience(): ExperienceContextValue {
  const value = useContext(ExperienceContext);
  if (!value) throw new Error('useExperience() precisa de <ExperienceProvider> acima.');
  return value;
}

export interface ExperienceProviderProps {
  children: ReactNode;
  /** Executa um "g d" etc. Recebe o id do item de `navigation.ts`. */
  onGo: (navId: string) => void;
}

export function ExperienceProvider({ children, onGo }: ExperienceProviderProps) {
  const { toast } = useToast();
  const prefsQuery = usePreferences();
  const prefs = prefsQuery.data;
  // Só o `mutate` (estável): o objeto da mutation muda a cada render e, numa dependência
  // de efeito, vira laço.
  const { mutate: saveDensity } = useSetDensity();
  const { mutate: saveDismiss } = useDismissHint();
  const { mutate: saveReset } = useResetHints();
  const { mutate: saveSeen } = useMarkChangelogSeen();
  /** Novidade já pedida como vista nesta sessão — abrir a gaveta duas vezes pede uma. */
  const seenRequested = useRef<string | null>(null);

  const [density, setLocalDensity] = useState<UiDensity>(readStoredDensity);
  const [dialog, setDialog] = useState<ExperienceDialog>(null);
  /** Fechadas nesta sessão, antes de o servidor confirmar (some na hora do toque). */
  const [optimisticHints, setOptimisticHints] = useState<ReadonlySet<string>>(new Set());

  // O servidor é a fonte: quando a preferência chega (ou muda em outro aparelho), vale ela.
  useEffect(() => {
    if (!prefs) return;
    setLocalDensity(prefs.density);
    storeDensity(prefs.density);
  }, [prefs]);

  const setDensity = useCallback(
    (value: UiDensity) => {
      const previous = density;
      setLocalDensity(value);
      storeDensity(value);
      saveDensity(
        { density: value },
        {
          onError: (error) => {
            setLocalDensity(previous);
            storeDensity(previous);
            toast({
              title: 'Não deu para salvar o layout',
              description: humanizeError(error),
              tone: 'danger',
            });
          },
        },
      );
    },
    [density, saveDensity, toast],
  );

  const isHintDismissed = useCallback(
    (hintId: string) => {
      if (optimisticHints.has(hintId)) return true;
      if (!prefs) return undefined;
      return prefs.dismissedHints.includes(hintId);
    },
    [optimisticHints, prefs],
  );

  const dismissHint = useCallback(
    (hintId: string) => {
      setOptimisticHints((current) => new Set(current).add(hintId));
      saveDismiss(
        { hintId },
        {
          onError: () => {
            // A dica volta: fingir que salvou faria ela reaparecer amanhã sem explicação.
            setOptimisticHints((current) => {
              const next = new Set(current);
              next.delete(hintId);
              return next;
            });
            toast({ title: 'Não deu para fechar a dica agora', tone: 'danger' });
          },
        },
      );
    },
    [saveDismiss, toast],
  );

  const resetHints = useCallback(() => {
    setOptimisticHints(new Set());
    saveReset(undefined, {
      onSuccess: () => toast({ title: 'As dicas vão aparecer de novo', tone: 'success' }),
      onError: (error) =>
        toast({
          title: 'Não deu para reativar as dicas',
          description: humanizeError(error),
          tone: 'danger',
        }),
    });
  }, [saveReset, toast]);

  // Sem preferência carregada, não acende o ponto: "não sei" não é "tem novidade".
  const hasNews = prefs ? hasUnreadChangelog(prefs.changelogSeenId) : false;

  const seenId = prefs?.changelogSeenId;
  const prefsLoaded = prefs !== undefined;
  const markNewsSeen = useCallback(() => {
    if (!prefsLoaded || !LATEST_CHANGELOG_ID || seenId === LATEST_CHANGELOG_ID) return;
    if (seenRequested.current === LATEST_CHANGELOG_ID) return;
    seenRequested.current = LATEST_CHANGELOG_ID;
    saveSeen(
      { entryId: LATEST_CHANGELOG_ID },
      {
        onError: () => {
          seenRequested.current = null;
        },
      },
    );
  }, [prefsLoaded, seenId, saveSeen]);

  const openDialog = useCallback((next: Exclude<ExperienceDialog, null>) => setDialog(next), []);
  const closeDialog = useCallback(() => setDialog(null), []);

  // ---------------------------------------------------------------- atalhos --
  const reader = useRef(createShortcutReader());
  const onGoRef = useRef(onGo);
  onGoRef.current = onGo;
  const dialogRef = useRef(dialog);
  dialogRef.current = dialog;

  useEffect(() => {
    const run = (action: ShortcutAction) => {
      if (action.kind === 'palette') setDialog('palette');
      else if (action.kind === 'help') setDialog('shortcuts');
      else onGoRef.current(action.navId);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) return;
      // Com um diálogo aberto, só o ⌘K age (fecha/reabre a busca); o resto é do diálogo.
      const anyModal =
        dialogRef.current !== null || document.querySelector('dialog[open]') !== null;
      const action = reader.current({
        key: event.key,
        metaKey: event.metaKey,
        ctrlKey: event.ctrlKey,
        altKey: event.altKey,
        inTextField: anyModal || isTextField(event.target),
        at: event.timeStamp || Date.now(),
      });
      if (!action) return;
      event.preventDefault();
      if (action.kind === 'palette' && dialogRef.current === 'palette') {
        setDialog(null);
        return;
      }
      run(action);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const value = useMemo<ExperienceContextValue>(
    () => ({
      density,
      setDensity,
      isHintDismissed,
      dismissHint,
      resetHints,
      hasNews,
      markNewsSeen,
      npsDue: prefs?.npsDue ?? false,
      dialog,
      openDialog,
      closeDialog,
    }),
    [
      density,
      setDensity,
      isHintDismissed,
      dismissHint,
      resetHints,
      hasNews,
      markNewsSeen,
      prefs?.npsDue,
      dialog,
      openDialog,
      closeDialog,
    ],
  );

  return (
    <ExperienceContext.Provider value={value}>
      <DensityProvider density={density}>
        <div data-density={density} className="contents">
          {children}
        </div>
      </DensityProvider>
    </ExperienceContext.Provider>
  );
}
